
// ==================== EXAM TYPES ====================

export type ExamType = 'cpale' | 'bar';

export interface ExamTypeConfig {
    id: ExamType;
    title: string;
    description: string;
    icon: string;
    subjects: MasterySubject[];
    enabled: boolean;
}

// ==================== CORE TYPES ====================

export interface ExamConfig {
    questionCount: number;
    passingScore: number; // Percentage (e.g., 75)
    duration: number; // In minutes
}

// Checklist item types for topic progression
export type ChecklistItemType = 'study' | 'question' | 'mini_quiz' | 'practice' | 'review';

export interface ChecklistItem {
    id: string;
    type: ChecklistItemType;
    title: string;
    description: string;
    // For 'question' type: a single conceptual question to answer
    question?: string;
    // For 'mini_quiz' type: number of questions to generate
    quizQuestionCount?: number;
    quizPassingScore?: number; // Percentage, default 70
    // For 'practice' type: a problem to solve
    practicePrompt?: string;
}

export interface ReadableResource {
    id: string;
    title: string;
    description: string;
    type: 'summary' | 'guide' | 'standard' | 'example' | 'comparison' | 'checklist' | 'case_study';
    icon: string; // emoji
    estimatedMinutes: number;
    content: string; // markdown content
}

export interface Subtopic {
    id: string;
    title: string;
    description: string;
    order: number;
    readables?: ReadableResource[];
}

export interface MasteryTopic {
    id: string;
    title: string;
    description: string;
    order: number;
    masteryPointsRequired: number; // Legacy: kept for backward compat
    finalExam: ExamConfig;
    keywords: string[];
    // New: Checklist-based progression
    subtopics: Subtopic[];
    checklist: ChecklistItem[];
    requiresNote: boolean; // User must write a note/summary before exam
    readables: ReadableResource[];
}

export interface MasterySubject {
    id: string;
    title: string;
    shortTitle: string;
    description: string;
    topics: MasteryTopic[];
    color: string;
    icon: string;
}

// ==================== MASTERY POINT EARNING CONFIG ====================

export interface MasteryPointsConfig {
    pointsPerChat: number;                // Base points per relevant AI conversation (default 2.5)
    quizBonusBase: number;                // Base mastery points for passing a quiz at 50%+ (min 20 items)
    quizBonusHighScore: number;           // Multiplier for 75%+ score (1.25 = 25% more)
    quizBonusDifficultyScale: {           // Additional multiplier by difficulty
        easy: number;
        medium: number;
        hard: number;
        expert: number;
    };
    quizMinItems: number;                 // Minimum quiz items to qualify for bonus (default 20)
    quizMinScore: number;                 // Minimum score % to earn any bonus (default 50)
    quizHighScoreThreshold: number;       // Score % for high-score bonus (default 75)
    mockExamBonusBase: number;            // Base mastery points for mock board exam
    mockExamBonusHighScore: number;       // Multiplier for 75%+ on mock exam
    topicCompletionBonus: number;         // Bonus points (tokens) for completing a topic
    subjectCompletionBonus: number;       // Bonus points (tokens) for completing all topics in a subject
    masteryThreshold: number;             // Points needed to unlock topic exam (default 200)
    examPassingScore: number;             // Passing % for mastery exams (default 75)
    examQuestionCount: number;            // Number of questions in mastery exam (default 10)
}

export const DEFAULT_MASTERY_POINTS_CONFIG: MasteryPointsConfig = {
    pointsPerChat: 2.5,
    quizBonusBase: 15,
    quizBonusHighScore: 1.25,
    quizBonusDifficultyScale: {
        easy: 1.0,
        medium: 1.25,
        hard: 1.5,
        expert: 2.0
    },
    quizMinItems: 20,
    quizMinScore: 50,
    quizHighScoreThreshold: 75,
    mockExamBonusBase: 25,
    mockExamBonusHighScore: 1.25,
    topicCompletionBonus: 500,
    subjectCompletionBonus: 5000,
    masteryThreshold: 200,
    examPassingScore: 75,
    examQuestionCount: 10
};

// ==================== MILESTONE REWARDS ====================

export interface MasteryMilestone {
    id: string;
    title: string;
    description: string;
    requirement: { type: 'points_total' | 'topics_completed' | 'subjects_completed' | 'exams_passed'; value: number };
    reward: { tokens: number; xp: number; badge?: string };
}

export const MASTERY_MILESTONES: MasteryMilestone[] = [
    { id: 'first-steps', title: 'First Steps', description: 'Earn your first 50 mastery points', requirement: { type: 'points_total', value: 50 }, reward: { tokens: 100, xp: 50 } },
    { id: 'getting-started', title: 'Getting Started', description: 'Earn 200 mastery points total', requirement: { type: 'points_total', value: 200 }, reward: { tokens: 250, xp: 100 } },
    { id: 'dedicated-learner', title: 'Dedicated Learner', description: 'Earn 500 mastery points total', requirement: { type: 'points_total', value: 500 }, reward: { tokens: 500, xp: 200 } },
    { id: 'knowledge-seeker', title: 'Knowledge Seeker', description: 'Earn 1,000 mastery points total', requirement: { type: 'points_total', value: 1000 }, reward: { tokens: 1000, xp: 500, badge: 'Knowledge Seeker' } },
    { id: 'mastery-grinder', title: 'Mastery Grinder', description: 'Earn 2,500 mastery points total', requirement: { type: 'points_total', value: 2500 }, reward: { tokens: 2000, xp: 1000, badge: 'Mastery Grinder' } },
    { id: 'scholar', title: 'Scholar', description: 'Earn 5,000 mastery points total', requirement: { type: 'points_total', value: 5000 }, reward: { tokens: 5000, xp: 2500, badge: 'Scholar' } },
    { id: 'first-topic', title: 'Topic Master', description: 'Complete your first topic', requirement: { type: 'topics_completed', value: 1 }, reward: { tokens: 300, xp: 150 } },
    { id: 'five-topics', title: 'Rising Expert', description: 'Complete 5 topics', requirement: { type: 'topics_completed', value: 5 }, reward: { tokens: 1000, xp: 500, badge: 'Rising Expert' } },
    { id: 'ten-topics', title: 'Seasoned Reviewer', description: 'Complete 10 topics', requirement: { type: 'topics_completed', value: 10 }, reward: { tokens: 2500, xp: 1000, badge: 'Seasoned Reviewer' } },
    { id: 'twenty-topics', title: 'Board Exam Ready', description: 'Complete 20 topics', requirement: { type: 'topics_completed', value: 20 }, reward: { tokens: 5000, xp: 2500, badge: 'Board Exam Ready' } },
    { id: 'first-subject', title: 'Subject Conqueror', description: 'Complete all topics in a subject', requirement: { type: 'subjects_completed', value: 1 }, reward: { tokens: 5000, xp: 3000, badge: 'Subject Conqueror' } },
    { id: 'three-subjects', title: 'Triple Threat', description: 'Complete 3 subjects', requirement: { type: 'subjects_completed', value: 3 }, reward: { tokens: 10000, xp: 5000, badge: 'Triple Threat' } },
    { id: 'all-subjects', title: 'CPA Master', description: 'Complete all 6 CPALE subjects', requirement: { type: 'subjects_completed', value: 6 }, reward: { tokens: 25000, xp: 15000, badge: 'CPA Master' } },
    { id: 'five-exams', title: 'Exam Veteran', description: 'Pass 5 mastery exams', requirement: { type: 'exams_passed', value: 5 }, reward: { tokens: 750, xp: 300 } },
    { id: 'fifteen-exams', title: 'Exam Specialist', description: 'Pass 15 mastery exams', requirement: { type: 'exams_passed', value: 15 }, reward: { tokens: 3000, xp: 1500, badge: 'Exam Specialist' } },
];

// ==================== RUNTIME CONFIG (loaded from admin/localStorage) ====================

let _runtimeConfig: MasteryPointsConfig = { ...DEFAULT_MASTERY_POINTS_CONFIG };

export function loadMasteryPointsConfig(): MasteryPointsConfig {
    try {
        const stored = localStorage.getItem('luca_mastery_points_config');
        if (stored) {
            _runtimeConfig = { ...DEFAULT_MASTERY_POINTS_CONFIG, ...JSON.parse(stored) };
        }
    } catch { /* use defaults */ }
    return _runtimeConfig;
}

export function saveMasteryPointsConfig(config: MasteryPointsConfig): void {
    _runtimeConfig = config;
    localStorage.setItem('luca_mastery_points_config', JSON.stringify(config));
}

export function getMasteryPointsConfig(): MasteryPointsConfig {
    return _runtimeConfig;
}

// Legacy exports for backward compat
export const MASTERY_POINTS_PER_CHAT = DEFAULT_MASTERY_POINTS_CONFIG.pointsPerChat;
export const MASTERY_EXAM_PASSING_SCORE = DEFAULT_MASTERY_POINTS_CONFIG.examPassingScore;

// ==================== CONSTANTS ====================

const MASTERY_THRESHOLD = DEFAULT_MASTERY_POINTS_CONFIG.masteryThreshold;

// Helper to create subtopics from keywords
const generateSubtopics = (id: string, keywords: string[]): Subtopic[] => {
    // Group keywords into logical subtopics (3-5 per topic)
    const chunkSize = Math.max(2, Math.ceil(keywords.length / 4));
    const subtopics: Subtopic[] = [];
    for (let i = 0; i < Math.min(4, Math.ceil(keywords.length / chunkSize)); i++) {
        const chunk = keywords.slice(i * chunkSize, (i + 1) * chunkSize);
        if (chunk.length === 0) break;
        subtopics.push({
            id: `${id}-sub-${i + 1}`,
            title: chunk.map(k => k.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')).slice(0, 2).join(' & '),
            description: `Covers: ${chunk.join(', ')}`,
            order: i + 1
        });
    }
    return subtopics;
};

// Helper to generate checklist items for a topic
const generateChecklist = (id: string, title: string, keywords: string[]): ChecklistItem[] => {
    const items: ChecklistItem[] = [
        {
            id: `${id}-cl-study`,
            type: 'study',
            title: `Read & Understand: ${title}`,
            description: `Study the core concepts of ${title} using the study guide.`
        },
        {
            id: `${id}-cl-q1`,
            type: 'question',
            title: 'Conceptual Check',
            description: `Answer a key conceptual question about ${title}.`,
            question: `Explain the key principles and main concepts of ${title} in your own words. How do they relate to ${keywords.slice(0, 3).join(', ')}?`
        },
        {
            id: `${id}-cl-quiz1`,
            type: 'mini_quiz',
            title: 'Mini Quiz: Fundamentals',
            description: `20-item multiple choice quiz on the basics of ${title}.`,
            quizQuestionCount: 20,
            quizPassingScore: 60
        },
        {
            id: `${id}-cl-practice`,
            type: 'practice',
            title: 'Practice Problem',
            description: `Solve a practice problem related to ${title}.`,
            practicePrompt: `Generate a board-exam-level practice problem about ${title} covering ${keywords.slice(0, 4).join(', ')}. Include the solution.`
        },
        {
            id: `${id}-cl-quiz2`,
            type: 'mini_quiz',
            title: 'Challenge Quiz: Application',
            description: `20-item multiple choice quiz testing application of ${title} concepts.`,
            quizQuestionCount: 20,
            quizPassingScore: 70
        },
        {
            id: `${id}-cl-review`,
            type: 'review',
            title: 'Final Review',
            description: `Review all subtopics and ensure you understand the connections between concepts in ${title}.`
        }
    ];
    return items;
};

// Helper to generate readable resources for a topic
const generateReadables = (id: string, title: string, desc: string, kw: string[]): ReadableResource[] => {
    const cap = (s: string) => s.replace(/\b\w/g, c => c.toUpperCase());
    const topTerms = kw.filter(k => k.length > 2).slice(0, 6);
    const standards = kw.filter(k => /^(pas|pfrs|ias|ifrs|psa|isqm)\s*\d*$/i.test(k.trim()));
    const laws = kw.filter(k => /(law|code|recto|maceda|train|create|nirc|civil code|corporation code)/i.test(k));
    const hasComp = kw.some(k => /(computation|formula|method|rate|cost|npv|irr|depreciation|amortization|variance|budget|break even|cvp|eoq|wacc|roi|fifo|weighted average|effective interest|ecl|reconciliation|bvps|cgt|fbt|vat payable|estate tax|donors tax|percentage of completion)/i.test(k));

    const readables: ReadableResource[] = [];

    // 1. Topic Overview (always present)
    readables.push({
        id: `${id}-read-overview`,
        title: `${title}: Complete Overview`,
        description: `A comprehensive summary of ${title} — what it is, why it matters, and the key concepts you need to master.`,
        type: 'summary',
        icon: '📖',
        estimatedMinutes: 5,
        content: [
            `## ${title}`,
            ``,
            `### What is ${title}?`,
            `${desc}`,
            ``,
            `### Why It Matters`,
            `${title} is a core topic tested in the CPALE board examination. Understanding this topic is essential for both the exam and professional practice. It covers the foundational principles and practical applications that every CPA must know.`,
            ``,
            `### Key Concepts`,
            ...topTerms.map((t, i) => `${i + 1}. **${cap(t)}** — A fundamental concept within ${title} that you must understand thoroughly.`),
            ``,
            `### How This Topic Connects`,
            `${title} builds on foundational accounting/legal principles and connects to related topics in the CPALE syllabus. Mastering this topic will help you understand more advanced concepts later.`,
            ``,
            `### Study Tips`,
            `- Focus on understanding the *why* behind each rule, not just memorizing`,
            `- Pay attention to exceptions — the CPALE loves testing edge cases`,
            `- Practice with real-world scenarios to solidify your understanding`,
            hasComp ? `- Work through computations step by step — don't skip steps` : `- Create your own mnemonics for key rules and classifications`,
        ].join('\n'),
    });

    // 2. Key Terms & Definitions
    readables.push({
        id: `${id}-read-terms`,
        title: `Key Terms & Definitions`,
        description: `Essential vocabulary and definitions for ${title}. Know these terms inside and out.`,
        type: 'guide',
        icon: '📝',
        estimatedMinutes: 4,
        content: [
            `## Key Terms: ${title}`,
            ``,
            `Master these terms before moving on to practice problems and quizzes.`,
            ``,
            ...topTerms.map(t => [
                `### ${cap(t)}`,
                `A key concept in ${title}. Understanding ${t} is critical for answering CPALE questions correctly. Make sure you can define it, explain when it applies, and identify it in exam scenarios.`,
                ``,
            ].join('\n')),
            `---`,
            ``,
            `> **Tip:** Try explaining each term in your own words without looking at the definitions. If you can't, review the concept again.`,
        ].join('\n'),
    });

    // 3. Standards/Law Reference (if applicable)
    if (standards.length > 0 || laws.length > 0) {
        const refs = [...standards, ...laws].slice(0, 4);
        readables.push({
            id: `${id}-read-standard`,
            title: `Standards & Legal Basis`,
            description: `The accounting standards and legal framework governing ${title}.`,
            type: 'standard',
            icon: '⚖️',
            estimatedMinutes: 6,
            content: [
                `## Standards & Legal Framework: ${title}`,
                ``,
                `### Applicable Standards/Laws`,
                ...refs.map(r => `- **${r.toUpperCase()}** — Governs key aspects of ${title}`),
                ``,
                `### Scope`,
                `These standards/laws define the recognition, measurement, presentation, and disclosure requirements for ${title}. The CPALE frequently tests specific provisions and their application.`,
                ``,
                `### Key Requirements`,
                `1. **Recognition** — When and how to recognize items related to ${title}`,
                `2. **Measurement** — How to measure (initial and subsequent)`,
                `3. **Presentation** — How to present in financial statements or reports`,
                `4. **Disclosure** — What must be disclosed in the notes`,
                ``,
                `### Common Exam Questions`,
                `- "Under ${refs[0]?.toUpperCase() || 'the applicable standard'}, which of the following is correct?"`,
                `- Questions testing exceptions and special cases`,
                `- Scenario-based questions requiring application of specific provisions`,
                ``,
                `> **Remember:** The CPALE tests your ability to *apply* standards, not just recite them. Focus on understanding the principles behind each requirement.`,
            ].join('\n'),
        });
    }

    // 4. Worked Examples (for computational topics)
    if (hasComp) {
        readables.push({
            id: `${id}-read-examples`,
            title: `Worked Examples & Computations`,
            description: `Step-by-step worked examples showing how to solve ${title} problems.`,
            type: 'example',
            icon: '🔢',
            estimatedMinutes: 8,
            content: [
                `## Worked Examples: ${title}`,
                ``,
                `### Approach to ${title} Problems`,
                `When solving ${title} problems on the CPALE, follow this systematic approach:`,
                ``,
                `1. **Identify** — What type of ${title} problem is this?`,
                `2. **Gather** — What data is given? What's missing?`,
                `3. **Select** — Which formula or method applies?`,
                `4. **Compute** — Work through the calculation step by step`,
                `5. **Verify** — Does the answer make sense? Check for reasonableness`,
                ``,
                `### Key Formulas & Methods`,
                ...topTerms.slice(0, 4).map(t => `- **${cap(t)}**: Understand the formula, when to use it, and common variations`),
                ``,
                `### Common Pitfalls`,
                `- Mixing up similar formulas or methods`,
                `- Forgetting to adjust for specific conditions or exceptions`,
                `- Rounding errors — follow the problem's instructions`,
                `- Not reading the question carefully — watch for "except" and "not"`,
                ``,
                `### Practice Strategy`,
                `Start with basic problems, then increase difficulty. Time yourself to simulate exam conditions. Aim to solve each problem in under 3 minutes.`,
                ``,
                `> **Pro Tip:** On the CPALE, if your computed answer doesn't match any choice, re-read the problem — you likely missed a detail.`,
            ].join('\n'),
        });
    }

    // 5. Comparison & Distinctions
    if (topTerms.length >= 2) {
        readables.push({
            id: `${id}-read-compare`,
            title: `Comparisons & Distinctions`,
            description: `Key differences and similarities between concepts in ${title}.`,
            type: 'comparison',
            icon: '⚡',
            estimatedMinutes: 5,
            content: [
                `## Comparisons: ${title}`,
                ``,
                `One of the most common CPALE question types asks you to distinguish between similar concepts. Here are the key comparisons for ${title}:`,
                ``,
                ...(topTerms.length >= 2 ? [
                    `### ${cap(topTerms[0])} vs. ${cap(topTerms[1])}`,
                    `These two concepts are frequently confused. The key distinction lies in their definition, scope, and application. Make sure you can identify which one applies in a given scenario.`,
                    ``,
                ] : []),
                ...(topTerms.length >= 4 ? [
                    `### ${cap(topTerms[2])} vs. ${cap(topTerms[3])}`,
                    `Another important distinction. Pay attention to the specific conditions that determine which concept applies.`,
                    ``,
                ] : []),
                `### Quick Comparison Table`,
                `| Aspect | ${cap(topTerms[0])} | ${cap(topTerms[1] || topTerms[0])} |`,
                `|--------|${'-'.repeat(cap(topTerms[0]).length + 2)}|${'-'.repeat(cap(topTerms[1] || topTerms[0]).length + 2)}|`,
                `| Definition | Specific to its context | Distinct from the other |`,
                `| When to use | Under specific conditions | Under different conditions |`,
                `| CPALE focus | Frequently tested | Frequently tested |`,
                ``,
                `> **Exam Strategy:** When you see "which of the following" questions, the wrong answers often confuse these distinctions. Knowing the differences gives you an edge.`,
            ].join('\n'),
        });
    }

    // 6. CPALE Exam Checklist
    readables.push({
        id: `${id}-read-checklist`,
        title: `CPALE Exam Checklist`,
        description: `Everything you must know about ${title} before taking the board exam.`,
        type: 'checklist',
        icon: '✅',
        estimatedMinutes: 3,
        content: [
            `## Exam Readiness Checklist: ${title}`,
            ``,
            `Use this checklist to verify you're ready for ${title} questions on the CPALE:`,
            ``,
            `### Fundamentals`,
            `- [ ] I can define all key terms: ${topTerms.slice(0, 4).map(t => cap(t)).join(', ')}`,
            `- [ ] I understand the underlying principles and rationale`,
            `- [ ] I know which standard/law governs this topic`,
            ``,
            `### Application`,
            hasComp
                ? `- [ ] I can solve basic ${title} computations without notes\n- [ ] I can handle multi-step problems\n- [ ] I know the key formulas and when to use each one`
                : `- [ ] I can apply the rules to unfamiliar scenarios\n- [ ] I can identify the correct treatment in tricky situations\n- [ ] I know the exceptions and when they apply`,
            ``,
            `### Exam Skills`,
            `- [ ] I can answer a question on ${title} in under 2 minutes`,
            `- [ ] I know the common traps and how to avoid them`,
            `- [ ] I scored 75%+ on a practice quiz for this topic`,
            ``,
            `### Connections`,
            `- [ ] I understand how ${title} relates to other CPALE topics`,
            `- [ ] I can handle questions that combine ${title} with other concepts`,
            ``,
            `> **Ready?** If you checked all boxes, you're prepared for ${title} on the CPALE. If not, review the unchecked areas.`,
        ].join('\n'),
    });

    return readables;
};

// Helper to create topic with defaults
const t = (id: string, title: string, desc: string, order: number, kw: string[]): MasteryTopic => ({
    id, title, description: desc, order,
    masteryPointsRequired: MASTERY_THRESHOLD, // Legacy
    finalExam: { questionCount: 10, passingScore: 75, duration: 30 },
    keywords: kw,
    subtopics: generateSubtopics(id, kw),
    checklist: generateChecklist(id, title, kw),
    requiresNote: true,
    readables: generateReadables(id, title, desc, kw)
});

export const CPALE_SYLLABUS: MasterySubject[] = [
    {
        id: 'far',
        title: 'Financial Accounting & Reporting',
        shortTitle: 'FAR',
        description: 'Frameworks, standards, and financial statement preparation.',
        color: 'blue',
        icon: 'FAR',
        topics: [
            t('far-1', 'Conceptual Framework', 'Objectives of financial reporting, qualitative characteristics, and underlying assumptions.', 1, ['conceptual framework', 'qualitative characteristics', 'faithful representation', 'relevance', 'accrual basis', 'going concern', 'reporting entity']),
            t('far-2', 'Philippine Accounting Standards Overview', 'PAS, PFRS adoption, hierarchy of standards, and first-time adoption.', 2, ['pas', 'pfrs', 'accounting standards', 'first time adoption', 'pfrs 1', 'standard setting', 'frsc']),
            t('far-3', 'Cash & Cash Equivalents', 'Definition, classification, bank reconciliation, and proof of cash.', 3, ['cash', 'cash equivalents', 'bank reconciliation', 'proof of cash', 'petty cash', 'imprest fund', 'outstanding checks', 'deposit in transit']),
            t('far-4', 'Receivables & Impairment', 'Trade receivables, notes receivable, factoring, pledging, and ECL model.', 4, ['receivables', 'accounts receivable', 'notes receivable', 'factoring', 'pledging', 'allowance', 'bad debts', 'aging method', 'expected credit loss', 'ecl']),
            t('far-5', 'Inventory Valuation', 'Cost formulas (FIFO, weighted average), LCNRV, and estimation methods.', 5, ['inventory', 'fifo', 'weighted average', 'lcnrv', 'net realizable value', 'gross profit method', 'retail inventory method', 'cost of goods sold']),
            t('far-6', 'Inventory Systems & Special Topics', 'Perpetual vs periodic, purchase commitments, consignment inventory.', 6, ['perpetual', 'periodic', 'purchase commitment', 'consignment', 'inventory count', 'goods in transit', 'fob shipping', 'fob destination']),
            t('far-7', 'PPE: Recognition & Measurement', 'Initial recognition, cost components, borrowing costs (PAS 23), and subsequent costs.', 7, ['ppe', 'property plant equipment', 'pas 16', 'recognition', 'borrowing costs', 'pas 23', 'component approach', 'subsequent expenditure', 'capitalization']),
            t('far-8', 'PPE: Depreciation & Derecognition', 'Depreciation methods, useful life changes, revaluation model, and disposal.', 8, ['depreciation', 'straight line', 'declining balance', 'units of production', 'useful life', 'residual value', 'revaluation', 'derecognition', 'impairment']),
            t('far-9', 'Intangible Assets', 'Recognition criteria, internally generated intangibles, amortization, and impairment.', 9, ['intangible', 'pas 38', 'research', 'development', 'amortization', 'patent', 'trademark', 'copyright', 'software costs', 'indefinite life']),
            t('far-10', 'Goodwill & Impairment Testing', 'Goodwill from business combinations, annual impairment testing, and CGUs.', 10, ['goodwill', 'impairment test', 'cash generating unit', 'cgu', 'recoverable amount', 'value in use', 'fair value less costs', 'ias 36']),
            t('far-11', 'Investment Property (PAS 40)', 'Definition, fair value model, cost model, and transfers.', 11, ['investment property', 'pas 40', 'fair value model', 'cost model', 'transfer', 'rental income', 'capital appreciation']),
            t('far-12', 'Biological Assets (PAS 41)', 'Agriculture, bearer plants, fair value measurement, and harvest.', 12, ['biological assets', 'pas 41', 'agriculture', 'bearer plant', 'agricultural produce', 'harvest', 'fair value', 'point of harvest']),
            t('far-13', 'Financial Instruments: Classification', 'PFRS 9 classification: amortized cost, FVOCI, FVPL, and business model test.', 13, ['financial instruments', 'pfrs 9', 'amortized cost', 'fvoci', 'fvpl', 'business model', 'sppi test', 'classification', 'debt instrument', 'equity instrument']),
            t('far-14', 'Financial Instruments: Measurement & ECL', 'Initial/subsequent measurement, effective interest method, and expected credit losses.', 14, ['effective interest', 'expected credit loss', 'ecl', 'stage 1', 'stage 2', 'stage 3', 'impairment', 'financial asset', 'fair value', 'transaction costs']),
            t('far-15', 'Derivatives & Hedge Accounting', 'Forwards, options, swaps, hedge types, and hedge effectiveness.', 15, ['derivative', 'hedge accounting', 'fair value hedge', 'cash flow hedge', 'forward contract', 'option', 'swap', 'hedge effectiveness', 'hedging instrument']),
            t('far-16', 'Current Liabilities', 'Trade payables, accrued expenses, current portion of long-term debt, and provisions.', 16, ['current liabilities', 'trade payables', 'accrued expenses', 'current portion', 'short term borrowings', 'unearned revenue', 'refund liability']),
            t('far-17', 'Provisions & Contingencies (PAS 37)', 'Recognition criteria, measurement, onerous contracts, and contingent liabilities/assets.', 17, ['provisions', 'contingencies', 'pas 37', 'constructive obligation', 'onerous contract', 'warranty provision', 'contingent liability', 'contingent asset', 'best estimate']),
            t('far-18', 'Bonds Payable', 'Issuance at par/premium/discount, effective interest amortization, and retirement.', 18, ['bonds payable', 'premium', 'discount', 'effective interest', 'amortization', 'bond retirement', 'callable bonds', 'convertible bonds', 'face value']),
            t('far-19', 'Shareholders Equity', 'Share capital, share premium, treasury shares, and retained earnings.', 19, ['equity', 'share capital', 'share premium', 'treasury shares', 'retained earnings', 'par value', 'no par', 'stock split', 'appropriation']),
            t('far-20', 'Dividends & Book Value per Share', 'Cash, property, stock dividends, liquidating dividends, and BVPS computation.', 20, ['dividends', 'cash dividend', 'stock dividend', 'property dividend', 'liquidating dividend', 'book value per share', 'bvps', 'dividend declaration']),
            t('far-21', 'Revenue Recognition (PFRS 15)', 'Five-step model, performance obligations, and transaction price allocation.', 21, ['revenue recognition', 'pfrs 15', 'five step model', 'performance obligation', 'transaction price', 'contract', 'variable consideration', 'standalone selling price']),
            t('far-22', 'Revenue: Special Topics', 'Contract costs, warranties, licenses, bill-and-hold, consignment, and principal vs agent.', 22, ['contract costs', 'warranty', 'license', 'bill and hold', 'consignment revenue', 'principal agent', 'right of return', 'repurchase agreement']),
            t('far-23', 'Statement of Financial Position', 'Classification, presentation, and current/non-current distinction (PAS 1).', 23, ['statement of financial position', 'pas 1', 'balance sheet', 'current', 'non-current', 'classification', 'presentation', 'offsetting']),
            t('far-24', 'Income Statement & OCI', 'Profit or loss, other comprehensive income, and reclassification adjustments.', 24, ['income statement', 'comprehensive income', 'oci', 'reclassification', 'profit or loss', 'single statement', 'two statement', 'discontinued operations']),
            t('far-25', 'Statement of Cash Flows', 'Operating, investing, financing activities, direct and indirect methods.', 25, ['cash flows', 'operating activities', 'investing activities', 'financing activities', 'direct method', 'indirect method', 'pas 7', 'non-cash transactions']),
            t('far-26', 'Leases: Lessee Accounting (PFRS 16)', 'Right-of-use assets, lease liabilities, and short-term/low-value exemptions.', 26, ['lease', 'pfrs 16', 'right of use', 'lease liability', 'lessee', 'short term lease', 'low value', 'incremental borrowing rate']),
            t('far-27', 'Leases: Lessor Accounting', 'Finance lease vs operating lease classification, and lessor measurement.', 27, ['lessor', 'finance lease', 'operating lease', 'lease classification', 'net investment', 'unearned income', 'sale and leaseback']),
            t('far-28', 'Employee Benefits (PAS 19)', 'Short-term benefits, post-employment benefits, defined benefit plans, and actuarial gains/losses.', 28, ['employee benefits', 'pas 19', 'defined benefit', 'defined contribution', 'pension', 'actuarial', 'remeasurement', 'past service cost', 'curtailment']),
            t('far-29', 'Share-Based Payment (PFRS 2)', 'Equity-settled, cash-settled, and measurement of share-based transactions.', 29, ['share based payment', 'pfrs 2', 'stock option', 'equity settled', 'cash settled', 'grant date', 'vesting period', 'fair value', 'compensation expense']),
            t('far-30', 'Accounting Policies, Estimates & Errors (PAS 8)', 'Changes in policy, changes in estimate, correction of errors, and retrospective application.', 30, ['pas 8', 'accounting policies', 'change in estimate', 'correction of errors', 'retrospective', 'prospective', 'prior period adjustment']),
        ]
    },
    {
        id: 'afar',
        title: 'Advanced Financial Accounting & Reporting',
        shortTitle: 'AFAR',
        description: 'Business combinations, partnerships, government accounting, and specialized industries.',
        color: 'indigo',
        icon: 'AFAR',
        topics: [
            t('afar-1', 'Business Combinations: Acquisition Method', 'Identifying the acquirer, consideration transferred, and acquisition-date fair values.', 1, ['business combination', 'pfrs 3', 'acquisition method', 'acquirer', 'consideration transferred', 'acquisition date', 'fair value']),
            t('afar-2', 'Goodwill & Bargain Purchase', 'Measuring goodwill, bargain purchase gain, and contingent consideration.', 2, ['goodwill', 'bargain purchase', 'contingent consideration', 'gain on acquisition', 'measurement period', 'provisional amounts']),
            t('afar-3', 'Consolidation: Basic Procedures', 'Elimination entries, NCI measurement, and consolidated statements at acquisition date.', 3, ['consolidation', 'elimination entries', 'non-controlling interest', 'nci', 'acquisition date', 'consolidated statements', 'pfrs 10']),
            t('afar-4', 'Consolidation: Subsequent Periods', 'Post-acquisition adjustments, intercompany transactions, and unrealized profits.', 4, ['subsequent period', 'intercompany', 'upstream', 'downstream', 'unrealized profit', 'inventory elimination', 'ppe elimination']),
            t('afar-5', 'Consolidation: Complex Structures', 'Indirect holdings, mutual holdings, and changes in ownership interest.', 5, ['indirect holding', 'mutual holding', 'change in ownership', 'step acquisition', 'partial disposal', 'loss of control', 'complex consolidation']),
            t('afar-6', 'Partnership: Formation & Operations', 'Capital contributions, profit/loss sharing, and partnership agreements.', 6, ['partnership', 'formation', 'capital contribution', 'profit sharing', 'salary allowance', 'interest on capital', 'bonus to partners']),
            t('afar-7', 'Partnership: Admission & Withdrawal', 'Bonus method, goodwill method, and revaluation of assets.', 7, ['admission', 'withdrawal', 'bonus method', 'goodwill method', 'revaluation', 'new partner', 'retiring partner']),
            t('afar-8', 'Partnership: Dissolution & Liquidation', 'Lump-sum liquidation, installment liquidation, and cash distribution plans.', 8, ['dissolution', 'liquidation', 'lump sum', 'installment liquidation', 'cash distribution plan', 'safe payment', 'loss absorption']),
            t('afar-9', 'Corporate Liquidation', 'Statement of affairs, deficiency accounts, and trustee/receiver accounting.', 9, ['corporate liquidation', 'statement of affairs', 'deficiency', 'trustee', 'receiver', 'secured creditor', 'unsecured creditor', 'realization']),
            t('afar-10', 'Joint Arrangements (PFRS 11)', 'Joint operations vs joint ventures, and accounting for each type.', 10, ['joint arrangement', 'pfrs 11', 'joint venture', 'joint operation', 'proportionate share', 'equity method']),
            t('afar-11', 'Investments in Associates (PAS 28)', 'Equity method, significant influence, and impairment of investments.', 11, ['associate', 'equity method', 'pas 28', 'significant influence', 'share of profit', 'impairment', 'investment in associate']),
            t('afar-12', 'Foreign Currency Transactions (PAS 21)', 'Functional currency, transaction gains/losses, and translation of foreign operations.', 12, ['foreign currency', 'pas 21', 'functional currency', 'exchange rate', 'transaction gain', 'transaction loss', 'translation', 'foreign operation']),
            t('afar-13', 'Government Accounting: NGAS Framework', 'Philippine government accounting system, fund structure, and COA regulations.', 13, ['government accounting', 'ngas', 'coa', 'commission on audit', 'fund accounting', 'general fund', 'appropriation', 'allotment']),
            t('afar-14', 'Government Accounting: Transactions & Reports', 'Budget cycle, obligation, disbursement, and government financial statements.', 14, ['budget cycle', 'obligation', 'disbursement', 'government financial statements', 'registry', 'journal entry voucher', 'trial balance']),
            t('afar-15', 'Not-for-Profit Organizations', 'Fund accounting for NPOs, restricted/unrestricted funds, and financial reporting.', 15, ['not for profit', 'npo', 'fund accounting', 'restricted fund', 'unrestricted fund', 'donation', 'grant', 'npo financial statements']),
            t('afar-16', 'Construction Contracts & Revenue', 'Percentage of completion, cost-to-cost method, and contract losses.', 16, ['construction contract', 'percentage of completion', 'cost to cost', 'contract revenue', 'contract loss', 'progress billing', 'retention']),
            t('afar-17', 'Franchise & Consignment Accounting', 'Franchise fee revenue, initial/continuing fees, consignment sales, and account sales.', 17, ['franchise', 'franchise fee', 'initial fee', 'continuing fee', 'consignment', 'consignor', 'consignee', 'account sales']),
            t('afar-18', 'Home Office & Branch Accounting', 'Reciprocal accounts, combined statements, and interoffice transactions.', 18, ['home office', 'branch accounting', 'reciprocal accounts', 'combined statements', 'interoffice', 'shipments to branch', 'allowance for overvaluation']),
            t('afar-19', 'Installment Sales', 'Installment method, cost recovery method, and repossession accounting.', 19, ['installment sales', 'installment method', 'cost recovery method', 'deferred gross profit', 'repossession', 'realized gross profit']),
            t('afar-20', 'Segment Reporting & Related Parties', 'PFRS 8 operating segments, quantitative thresholds, and PAS 24 related party disclosures.', 20, ['segment reporting', 'pfrs 8', 'operating segment', 'quantitative threshold', 'related party', 'pas 24', 'key management', 'disclosure']),
        ]
    },
    {
        id: 'aud',
        title: 'Auditing',
        shortTitle: 'AUD',
        description: 'Audit engagements, internal controls, evidence, and professional standards.',
        color: 'emerald',
        icon: 'AUD',
        topics: [
            t('aud-1', 'Nature & Objectives of Auditing', 'Definition, types of audits, and reasonable assurance concept.', 1, ['auditing', 'reasonable assurance', 'types of audit', 'financial statement audit', 'compliance audit', 'operational audit', 'assurance engagement']),
            t('aud-2', 'Professional Standards & Regulations', 'PSA framework, AASC, IAASB standards, and regulatory environment.', 2, ['psa', 'aasc', 'iaasb', 'auditing standards', 'regulatory', 'quality management', 'isqm', 'engagement standards']),
            t('aud-3', 'Professional Ethics & Independence', 'Code of ethics, threats, safeguards, and independence requirements.', 3, ['professional ethics', 'independence', 'code of ethics', 'threats', 'safeguards', 'integrity', 'objectivity', 'confidentiality', 'professional behavior']),
            t('aud-4', 'Engagement Acceptance & Planning', 'Client acceptance, engagement letters, and overall audit strategy.', 4, ['engagement acceptance', 'engagement letter', 'audit planning', 'overall strategy', 'preconditions', 'terms of engagement', 'predecessor auditor']),
            t('aud-5', 'Understanding the Entity & Its Environment', 'Industry knowledge, internal/external factors, and business risk assessment.', 5, ['understanding entity', 'environment', 'industry', 'business risk', 'internal factors', 'external factors', 'risk of material misstatement']),
            t('aud-6', 'Materiality', 'Planning materiality, performance materiality, and tolerable misstatement.', 6, ['materiality', 'planning materiality', 'performance materiality', 'tolerable misstatement', 'clearly trivial', 'revision of materiality']),
            t('aud-7', 'Risk Assessment Procedures', 'Identifying and assessing risks of material misstatement at assertion level.', 7, ['risk assessment', 'inherent risk', 'control risk', 'detection risk', 'audit risk model', 'assertion level', 'significant risk', 'risk of material misstatement']),
            t('aud-8', 'Internal Control: Framework', 'COSO components, entity-level controls, and IT general controls.', 8, ['internal control', 'coso', 'control environment', 'risk assessment', 'control activities', 'monitoring', 'information communication', 'it controls']),
            t('aud-9', 'Internal Control: Testing & Evaluation', 'Tests of controls, operating effectiveness, and control deficiencies.', 9, ['test of controls', 'operating effectiveness', 'control deficiency', 'significant deficiency', 'material weakness', 'walkthrough', 'design effectiveness']),
            t('aud-10', 'Audit Evidence: Concepts', 'Sufficiency, appropriateness, reliability, and types of audit evidence.', 10, ['audit evidence', 'sufficiency', 'appropriateness', 'reliability', 'external confirmation', 'documentary evidence', 'assertions']),
            t('aud-11', 'Substantive Procedures', 'Tests of details, substantive analytical procedures, and dual-purpose tests.', 11, ['substantive procedures', 'tests of details', 'analytical procedures', 'substantive testing', 'dual purpose test', 'vouching', 'tracing']),
            t('aud-12', 'Audit Procedures by Cycle', 'Revenue, expenditure, payroll, inventory, and financing cycle procedures.', 12, ['revenue cycle', 'expenditure cycle', 'payroll cycle', 'inventory cycle', 'financing cycle', 'confirmation', 'cutoff testing', 'physical count']),
            t('aud-13', 'Audit Sampling', 'Statistical vs non-statistical, attribute sampling, variables sampling, and evaluation.', 13, ['audit sampling', 'statistical sampling', 'non-statistical', 'attribute sampling', 'variables sampling', 'sample size', 'sampling risk', 'tolerable rate']),
            t('aud-14', 'Audit of Accounting Estimates', 'Management estimates, fair value measurements, and related disclosures.', 14, ['accounting estimates', 'fair value', 'estimation uncertainty', 'management bias', 'point estimate', 'range of estimates', 'retrospective review']),
            t('aud-15', 'Subsequent Events & Going Concern', 'Types of subsequent events, going concern assessment, and management plans.', 15, ['subsequent events', 'going concern', 'adjusting events', 'non-adjusting events', 'management plans', 'material uncertainty', 'liquidation basis']),
            t('aud-16', 'Written Representations & Documentation', 'Management representation letter, audit documentation, and working papers.', 16, ['written representations', 'management representation', 'audit documentation', 'working papers', 'current file', 'permanent file', 'retention']),
            t('aud-17', 'Audit Reports: Unmodified Opinion', 'Elements of the report, key audit matters, and other information.', 17, ['audit report', 'unmodified opinion', 'key audit matters', 'kam', 'other information', 'report elements', 'basis for opinion']),
            t('aud-18', 'Audit Reports: Modified Opinions', 'Qualified, adverse, disclaimer, and emphasis of matter/other matter paragraphs.', 18, ['modified opinion', 'qualified opinion', 'adverse opinion', 'disclaimer', 'emphasis of matter', 'other matter', 'scope limitation', 'material misstatement']),
            t('aud-19', 'Other Assurance Engagements', 'Reviews, agreed-upon procedures, compilations, and other attestation services.', 19, ['review engagement', 'agreed upon procedures', 'compilation', 'limited assurance', 'negative assurance', 'attestation', 'psre']),
            t('aud-20', 'Internal Auditing & Governance', 'Internal audit function, governance, risk management, and using internal auditors work.', 20, ['internal audit', 'governance', 'risk management', 'internal audit function', 'audit committee', 'using work of internal auditors', 'ippf']),
        ]
    },
    {
        id: 'tax',
        title: 'Taxation',
        shortTitle: 'TAX',
        description: 'Philippine taxation: income tax, business tax, transfer tax, and remedies.',
        color: 'red',
        icon: 'TAX',
        topics: [
            t('tax-1', 'General Principles of Taxation', 'Nature, inherent powers, constitutional limitations, and tax doctrines.', 1, ['taxation principles', 'inherent powers', 'police power', 'eminent domain', 'constitutional limitations', 'tax doctrines', 'lifeblood doctrine']),
            t('tax-2', 'Situs & Double Taxation', 'Situs rules, double taxation, tax treaties, and tax credits.', 2, ['situs', 'double taxation', 'tax treaty', 'tax credit', 'source rules', 'within the philippines', 'without the philippines']),
            t('tax-3', 'Income Tax: Gross Income', 'Inclusions, exclusions, and sources of gross income.', 3, ['gross income', 'inclusions', 'exclusions', 'compensation income', 'business income', 'passive income', 'capital gains', 'income from whatever source']),
            t('tax-4', 'Income Tax: Deductions', 'Itemized deductions, OSD, and special deductions.', 4, ['deductions', 'itemized deduction', 'optional standard deduction', 'osd', 'business expenses', 'depreciation', 'bad debts', 'charitable contributions']),
            t('tax-5', 'Individual Income Taxation', 'Graduated rates, TRAIN law changes, and computation for individuals.', 5, ['individual taxation', 'graduated tax', 'train law', 'compensation income', 'self-employed', 'mixed income', 'tax rate schedule', '8% flat rate']),
            t('tax-6', 'Corporate Income Tax', 'Regular corporate income tax, MCIT, IAET, and CREATE law.', 6, ['corporate income tax', 'rcit', 'mcit', 'iaet', 'create law', 'domestic corporation', '25% tax rate', 'quarterly returns']),
            t('tax-7', 'Special Corporations', 'Resident foreign, non-resident foreign, and special tax regimes.', 7, ['special corporations', 'resident foreign', 'non-resident foreign', 'branch profit remittance', 'regional headquarters', 'peza', 'special economic zone']),
            t('tax-8', 'Withholding Tax System', 'Creditable withholding, expanded withholding, and withholding on compensation.', 8, ['withholding tax', 'creditable withholding', 'expanded withholding', 'withholding on compensation', 'ewt', 'cwt', 'bir form 2307']),
            t('tax-9', 'Final Withholding Tax', 'Final tax on passive income: interest, dividends, royalties, and prizes.', 9, ['final tax', 'passive income', 'interest income', 'dividend', 'royalty', 'prizes', 'capital gains tax', 'final withholding']),
            t('tax-10', 'Capital Gains Tax', 'CGT on real property, shares of stock, and other capital assets.', 10, ['capital gains tax', 'cgt', 'real property', 'shares of stock', 'listed shares', 'unlisted shares', 'capital asset', '6% cgt', 'stock transaction tax']),
            t('tax-11', 'Fringe Benefits Tax', 'Taxable fringe benefits, de minimis benefits, and FBT computation.', 11, ['fringe benefits tax', 'fbt', 'de minimis', 'taxable fringe benefits', 'grossed up monetary value', 'rank and file', 'managerial']),
            t('tax-12', 'Value-Added Tax: Basic Concepts', 'VAT-able transactions, VAT threshold, and zero-rated vs exempt.', 12, ['vat', 'value added tax', 'vat threshold', 'vatable', 'zero rated', 'vat exempt', 'sale of goods', 'sale of services']),
            t('tax-13', 'VAT: Input/Output & Compliance', 'Input VAT, output VAT, VAT payable computation, and filing.', 13, ['input vat', 'output vat', 'vat payable', 'excess input', 'vat refund', 'vat return', 'quarterly vat', 'bir form 2550']),
            t('tax-14', 'Other Percentage Taxes & Excise Tax', 'Percentage tax, excise tax on goods, and documentary stamp tax.', 14, ['percentage tax', 'excise tax', 'documentary stamp tax', 'dst', 'specific tax', 'ad valorem', '3% percentage tax']),
            t('tax-15', 'Estate Tax', 'Gross estate, deductions, net estate, and estate tax computation.', 15, ['estate tax', 'gross estate', 'net estate', 'deductions from estate', 'standard deduction', 'family home', 'conjugal', 'community property', '6% estate tax']),
            t('tax-16', 'Donor\'s Tax', 'Taxable donations, exemptions, and donor\'s tax computation.', 16, ['donors tax', 'donation', 'donee', 'donor', 'exempt donation', 'stranger', 'relative', '6% donors tax', 'net gift']),
            t('tax-17', 'Tax Remedies: Government', 'Assessment process, collection remedies, and prescriptive periods.', 17, ['tax remedies', 'assessment', 'preliminary assessment', 'formal assessment', 'collection', 'distraint', 'levy', 'prescription']),
            t('tax-18', 'Tax Remedies: Taxpayer', 'Protest, refund, appeal to CTA, and compromise/abatement.', 18, ['taxpayer remedies', 'protest', 'refund', 'cta', 'court of tax appeals', 'compromise', 'abatement', 'appeal', 'administrative protest']),
            t('tax-19', 'Local & Real Property Taxation', 'Local government taxation, real property tax, and community tax.', 19, ['local taxation', 'real property tax', 'community tax', 'local government code', 'business tax', 'provincial tax', 'municipal tax']),
            t('tax-20', 'Tax Compliance & Returns', 'Filing requirements, BIR forms, deadlines, and penalties.', 20, ['tax compliance', 'tax return', 'bir forms', 'filing deadline', 'penalties', 'surcharge', 'interest', 'compromise penalty', 'annual return']),
        ]
    },
    {
        id: 'mas',
        title: 'Management Advisory Services',
        shortTitle: 'MAS',
        description: 'Cost accounting, financial management, and strategic decision-making.',
        color: 'amber',
        icon: 'MAS',
        topics: [
            t('mas-1', 'Cost Classification & Behavior', 'Cost types, cost behavior patterns, and cost estimation methods.', 1, ['cost classification', 'variable cost', 'fixed cost', 'mixed cost', 'cost behavior', 'high low method', 'regression', 'cost estimation']),
            t('mas-2', 'Relevant Costs & Decision Making', 'Relevant vs irrelevant costs, sunk costs, opportunity costs, and differential analysis.', 2, ['relevant cost', 'sunk cost', 'opportunity cost', 'differential cost', 'incremental cost', 'avoidable cost', 'decision making']),
            t('mas-3', 'Cost-Volume-Profit Analysis', 'Break-even, contribution margin, target profit, and operating leverage.', 3, ['cvp', 'cost volume profit', 'break even', 'contribution margin', 'target profit', 'margin of safety', 'operating leverage']),
            t('mas-4', 'CVP: Multi-Product & Special Topics', 'Sales mix, multi-product break-even, and CVP under uncertainty.', 4, ['sales mix', 'multi product', 'weighted average contribution', 'cvp assumptions', 'sensitivity analysis', 'indifference point']),
            t('mas-5', 'Job Order Costing', 'Job cost sheets, overhead application, and over/under-applied overhead.', 5, ['job order costing', 'job cost sheet', 'overhead application', 'predetermined rate', 'over applied', 'under applied', 'actual costing', 'normal costing']),
            t('mas-6', 'Process Costing', 'Equivalent units, FIFO vs weighted average, and transferred-in costs.', 6, ['process costing', 'equivalent units', 'fifo method', 'weighted average method', 'transferred in costs', 'cost of production report', 'conversion costs']),
            t('mas-7', 'Spoilage, Rework & Scrap', 'Normal vs abnormal spoilage, rework costs, and scrap accounting.', 7, ['spoilage', 'normal spoilage', 'abnormal spoilage', 'rework', 'scrap', 'defective units', 'inspection point']),
            t('mas-8', 'Activity-Based Costing', 'Cost pools, cost drivers, ABC vs traditional costing, and ABM.', 8, ['activity based costing', 'abc', 'cost pool', 'cost driver', 'activity based management', 'abm', 'overhead allocation', 'traditional costing']),
            t('mas-9', 'Standard Costing: Materials & Labor', 'Setting standards, material price/quantity variances, and labor rate/efficiency variances.', 9, ['standard costing', 'material variance', 'price variance', 'quantity variance', 'labor variance', 'rate variance', 'efficiency variance', 'standard cost']),
            t('mas-10', 'Standard Costing: Overhead Variances', 'Variable/fixed overhead variances, two-way, three-way, and four-way analysis.', 10, ['overhead variance', 'spending variance', 'efficiency variance', 'volume variance', 'budget variance', 'two way analysis', 'three way analysis', 'four way analysis']),
            t('mas-11', 'Master Budgeting', 'Sales budget, production budget, cash budget, and pro forma statements.', 11, ['master budget', 'sales budget', 'production budget', 'cash budget', 'budgeted income statement', 'budgeted balance sheet', 'pro forma']),
            t('mas-12', 'Flexible Budgets & Performance', 'Flexible budget variances, static budget, and performance evaluation.', 12, ['flexible budget', 'static budget', 'budget variance', 'volume variance', 'performance evaluation', 'controllable variance']),
            t('mas-13', 'Responsibility Accounting', 'Cost centers, profit centers, investment centers, and segment reporting.', 13, ['responsibility accounting', 'cost center', 'profit center', 'investment center', 'segment reporting', 'controllable costs', 'common costs']),
            t('mas-14', 'Transfer Pricing', 'Market-based, cost-based, negotiated, and dual pricing methods.', 14, ['transfer pricing', 'market based', 'cost based', 'negotiated price', 'dual pricing', 'minimum transfer price', 'maximum transfer price', 'divisional autonomy']),
            t('mas-15', 'ROI, RI & EVA', 'Return on investment, residual income, economic value added, and balanced scorecard.', 15, ['roi', 'return on investment', 'residual income', 'eva', 'economic value added', 'balanced scorecard', 'dupont analysis', 'asset turnover']),
            t('mas-16', 'Working Capital Management', 'Cash management, receivables management, inventory management, and short-term financing.', 16, ['working capital', 'cash management', 'receivables management', 'inventory management', 'eoq', 'cash conversion cycle', 'short term financing']),
            t('mas-17', 'Capital Budgeting', 'NPV, IRR, payback period, profitability index, and project evaluation.', 17, ['capital budgeting', 'npv', 'irr', 'payback period', 'profitability index', 'net present value', 'internal rate of return', 'discount rate']),
            t('mas-18', 'Cost of Capital & Capital Structure', 'WACC, cost of equity, cost of debt, and optimal capital structure.', 18, ['cost of capital', 'wacc', 'cost of equity', 'cost of debt', 'capital structure', 'leverage', 'capm', 'dividend growth model']),
            t('mas-19', 'Special Decision Analysis', 'Make or buy, special orders, sell or process further, and product mix decisions.', 19, ['make or buy', 'special order', 'sell or process further', 'product mix', 'scarce resources', 'constraint', 'outsourcing', 'accept or reject']),
            t('mas-20', 'Strategic Cost Management', 'Target costing, kaizen, value chain, life cycle costing, and quality costs.', 20, ['target costing', 'kaizen', 'value chain', 'life cycle costing', 'quality costs', 'prevention costs', 'appraisal costs', 'failure costs', 'total quality management']),
        ]
    },
    {
        id: 'rfbt',
        title: 'Regulatory Framework for Business Transactions',
        shortTitle: 'RFBT',
        description: 'Obligations, contracts, business organizations, negotiable instruments, and special laws.',
        color: 'rose',
        icon: 'RFBT',
        topics: [
            t('rfbt-1', 'Obligations: Nature & Sources', 'Definition, elements, sources (law, contracts, quasi-contracts, delicts, quasi-delicts).', 1, ['obligations', 'sources of obligations', 'law', 'contracts', 'quasi-contract', 'delict', 'quasi-delict', 'civil code']),
            t('rfbt-2', 'Kinds of Obligations', 'Pure, conditional, with a period, alternative, joint, and solidary obligations.', 2, ['pure obligation', 'conditional obligation', 'suspensive', 'resolutory', 'period', 'alternative', 'joint obligation', 'solidary obligation']),
            t('rfbt-3', 'Extinguishment of Obligations', 'Payment, loss, condonation, confusion, compensation, and novation.', 3, ['extinguishment', 'payment', 'loss of thing', 'condonation', 'confusion', 'compensation', 'novation', 'tender of payment']),
            t('rfbt-4', 'Contracts: General Provisions', 'Essential requisites, stages, classification, and defective contracts.', 4, ['contracts', 'essential requisites', 'consent', 'object', 'cause', 'voidable', 'void', 'unenforceable', 'rescissible']),
            t('rfbt-5', 'Breach of Contract & Damages', 'Types of breach, remedies, and kinds of damages.', 5, ['breach', 'damages', 'actual damages', 'moral damages', 'exemplary damages', 'nominal damages', 'temperate damages', 'liquidated damages', 'rescission']),
            t('rfbt-6', 'Contract of Sale: Essentials', 'Elements, stages, perfection, and distinction from contract to sell.', 6, ['contract of sale', 'elements of sale', 'perfection', 'contract to sell', 'earnest money', 'option money', 'subject matter', 'price']),
            t('rfbt-7', 'Sales: Warranties & Remedies', 'Express/implied warranties, unpaid seller remedies, and buyer remedies.', 7, ['warranty', 'express warranty', 'implied warranty', 'merchantability', 'fitness', 'unpaid seller', 'buyer remedies', 'caveat emptor']),
            t('rfbt-8', 'Recto Law & Maceda Law', 'Installment sales of movables and immovables, and buyer protections.', 8, ['recto law', 'maceda law', 'installment sales', 'movable property', 'immovable property', 'grace period', 'cash surrender value', 'cancellation']),
            t('rfbt-9', 'Law on Agency', 'Creation, kinds, obligations of principal/agent, and modes of extinguishment.', 9, ['agency', 'principal', 'agent', 'express agency', 'implied agency', 'special agency', 'general agency', 'ratification', 'revocation']),
            t('rfbt-10', 'Credit Transactions: Pledge & Mortgage', 'Real mortgage, chattel mortgage, pledge, and antichresis.', 10, ['pledge', 'mortgage', 'real estate mortgage', 'chattel mortgage', 'antichresis', 'security', 'foreclosure', 'redemption']),
            t('rfbt-11', 'Guaranty & Suretyship', 'Nature, kinds, effects, and extinguishment of guaranty and suretyship.', 11, ['guaranty', 'suretyship', 'guarantor', 'surety', 'benefit of excussion', 'solidary liability', 'extinguishment']),
            t('rfbt-12', 'Partnership Law', 'Formation, types, rights/obligations of partners, and dissolution.', 12, ['partnership', 'general partnership', 'limited partnership', 'partner rights', 'partner obligations', 'dissolution', 'winding up']),
            t('rfbt-13', 'Corporation Law: Formation & Structure', 'Revised Corporation Code, incorporation, corporate powers, and governance.', 13, ['corporation', 'revised corporation code', 'articles of incorporation', 'by-laws', 'corporate powers', 'board of directors', 'incorporators']),
            t('rfbt-14', 'Corporation Law: Shares & Stockholders', 'Types of shares, stockholder rights, dividends, and stock transactions.', 14, ['shares of stock', 'stockholder rights', 'dividends', 'pre-emptive right', 'appraisal right', 'treasury shares', 'stock certificate']),
            t('rfbt-15', 'Corporation Law: Special Topics', 'Mergers, consolidation, dissolution, one person corporation, and foreign corporations.', 15, ['merger', 'consolidation', 'dissolution', 'one person corporation', 'opc', 'foreign corporation', 'close corporation', 'non-stock corporation']),
            t('rfbt-16', 'Negotiable Instruments: Requisites', 'Form requirements, negotiability, and types of instruments.', 16, ['negotiable instruments', 'requisites', 'promissory note', 'bill of exchange', 'check', 'negotiability', 'unconditional promise', 'sum certain']),
            t('rfbt-17', 'Negotiable Instruments: Transfer & HDC', 'Negotiation, endorsement, holder in due course, and real/personal defenses.', 17, ['negotiation', 'endorsement', 'holder in due course', 'hdc', 'real defense', 'personal defense', 'blank endorsement', 'special endorsement']),
            t('rfbt-18', 'Negotiable Instruments: Liabilities', 'Liability of parties, presentment, dishonor, and notice of dishonor.', 18, ['liability of parties', 'presentment', 'dishonor', 'notice of dishonor', 'protest', 'drawer liability', 'endorser liability', 'accommodation party']),
            t('rfbt-19', 'Insurance Law', 'Insurable interest, types of insurance, and insurance contract principles.', 19, ['insurance', 'insurable interest', 'insurance contract', 'premium', 'indemnity', 'subrogation', 'concealment', 'warranty', 'insurance code']),
            t('rfbt-20', 'Special Commercial Laws', 'Data Privacy Act, E-Commerce Act, AMLA, Truth in Lending, and competition law.', 20, ['data privacy', 'e-commerce', 'amla', 'anti-money laundering', 'truth in lending', 'consumer act', 'competition act', 'intellectual property', 'bsp']),
        ]
    }
];

// ==================== EXAM TYPE REGISTRY ====================

export const EXAM_TYPES: ExamTypeConfig[] = [
    {
        id: 'cpale',
        title: 'CPA Licensure Examination',
        description: 'Philippine CPA Board Exam - 6 subjects covering accounting, auditing, taxation, and law.',
        icon: '🎓',
        subjects: CPALE_SYLLABUS,
        enabled: true
    },
    {
        id: 'bar',
        title: 'Bar Examination',
        description: 'Philippine Bar Exam - Coming soon.',
        icon: '⚖️',
        subjects: [], // To be populated later
        enabled: false
    }
];

// ==================== HELPERS ====================

export function getSubjectById(subjectId: string): MasterySubject | undefined {
    return CPALE_SYLLABUS.find(s => s.id === subjectId);
}

export function getTopicById(subjectId: string, topicId: string): MasteryTopic | undefined {
    const subject = getSubjectById(subjectId);
    return subject?.topics.find(t => t.id === topicId);
}

export function getAllTopicKeywords(subjectId: string, topicId: string): string[] {
    const topic = getTopicById(subjectId, topicId);
    return topic?.keywords || [];
}

// ==================== GUIDED LEARNING SYSTEM ====================

export interface PromptCard {
    text: string;
    /** What concept this prompt targets */
    context?: string;
}

export interface MicroGoal {
    id: string;
    text: string;
}

export interface QuickRefCard {
    label: string;
    content: string;
}

export interface StudyPhase {
    id: string;
    title: string;
    description: string;
    type: 'read' | 'chat' | 'practice' | 'quiz' | 'exam';
    difficulty: 'foundation' | 'intermediate' | 'advanced' | 'exam-level';
    /** What the user should be able to do after this phase */
    goalText: string;
    /** Micro-goals the user can check off within this phase */
    microGoals: MicroGoal[];
    /** Suggested prompts with context about what they target */
    prompts: PromptCard[];
    /** Quick-reference cards (formulas, mnemonics, rules-of-thumb) */
    quickRef?: QuickRefCard[];
    /** Estimated minutes */
    estimatedMinutes: number;
}

export interface TopicStudyGuide {
    topicId: string;
    subjectId: string;
    title: string;
    overview: string;
    /** Concrete, measurable learning objectives */
    learningObjectives: string[];
    phases: StudyPhase[];
    /** Key terms grouped by concept area */
    conceptGroups: { label: string; terms: string[] }[];
    /** Total estimated study time */
    totalMinutes: number;
}

// ==================== SMART CONTENT HELPERS ====================

/** Split keywords into logical concept groups of 2-4 terms */
function buildConceptGroups(keywords: string[], topicTitle: string): { label: string; terms: string[] }[] {
    const meaningful = keywords.filter(k => k.length > 2);
    if (meaningful.length <= 4) return [{ label: topicTitle, terms: meaningful }];

    const groups: { label: string; terms: string[] }[] = [];
    // First term is usually the broadest concept — use it as group 1 label
    const chunkSize = Math.ceil(meaningful.length / Math.ceil(meaningful.length / 3));
    for (let i = 0; i < meaningful.length; i += chunkSize) {
        const chunk = meaningful.slice(i, i + chunkSize);
        groups.push({
            label: chunk[0].replace(/\b\w/g, c => c.toUpperCase()),
            terms: chunk,
        });
    }
    return groups;
}

/** Detect if topic is computational, theoretical, or mixed based on keywords */
function detectTopicNature(keywords: string[]): 'computational' | 'theoretical' | 'mixed' {
    const compSignals = ['computation', 'formula', 'method', 'rate', 'cost', 'npv', 'irr', 'depreciation',
        'amortization', 'variance', 'budget', 'break even', 'cvp', 'eoq', 'wacc', 'roi', 'fifo',
        'weighted average', 'effective interest', 'ecl', 'reconciliation', 'proof of cash', 'bvps',
        'gross profit method', 'retail inventory', 'equivalent units', 'overhead', 'cgt', 'fbt',
        'vat payable', 'estate tax', 'donors tax', 'percentage of completion'];
    const theoSignals = ['framework', 'principles', 'ethics', 'independence', 'governance', 'standard',
        'classification', 'recognition', 'definition', 'nature', 'obligations', 'contracts',
        'law', 'code', 'doctrine', 'rights', 'remedies', 'assurance', 'opinion', 'report'];

    const kwLower = keywords.map(k => k.toLowerCase());
    const compHits = compSignals.filter(s => kwLower.some(k => k.includes(s))).length;
    const theoHits = theoSignals.filter(s => kwLower.some(k => k.includes(s))).length;

    if (compHits >= 3 && compHits > theoHits) return 'computational';
    if (theoHits >= 3 && theoHits > compHits) return 'theoretical';
    return 'mixed';
}

/** Detect relevant standard/law references from keywords */
function detectStandards(keywords: string[]): string[] {
    const stdPattern = /^(pas|pfrs|ias|ifrs|psa|isqm|ippf)\s*\d*$/i;
    const lawPattern = /(law|code|recto|maceda|train|create|civil code|corporation code|nirc)/i;
    return keywords.filter(k => stdPattern.test(k.trim()) || lawPattern.test(k));
}

/** Build concept-specific prompts that are actually useful */
function buildConceptPrompts(term: string, topicTitle: string, nature: string): PromptCard[] {
    const t = term.replace(/\b\w/g, c => c.toUpperCase());
    const prompts: PromptCard[] = [];

    prompts.push({
        text: `What exactly is "${term}" and how does it work in the context of ${topicTitle}? Give me the definition, key rules, and a concrete example.`,
        context: `Core definition of ${t}`,
    });

    if (nature === 'computational' || nature === 'mixed') {
        prompts.push({
            text: `Show me a step-by-step computation involving ${term}. Start with a simple example, then show a harder one.`,
            context: `${t} — computation practice`,
        });
    }

    if (nature === 'theoretical' || nature === 'mixed') {
        prompts.push({
            text: `What are the key rules, exceptions, and common exam traps related to "${term}"? List them clearly.`,
            context: `${t} — rules & exceptions`,
        });
    }

    return prompts;
}

// ==================== MAIN GUIDE GENERATOR ====================

/**
 * Generate a structured, topic-aware study guide.
 * Builds phases dynamically using keyword analysis to create
 * concept-specific prompts, micro-goals, and quick-reference cards.
 */
export function generateTopicStudyGuide(subjectId: string, topicId: string): TopicStudyGuide | null {
    const subject = getSubjectById(subjectId);
    const topic = subject?.topics.find(t => t.id === topicId);
    if (!subject || !topic) return null;

    const T = topic.title;
    const S = subject.shortTitle;
    const kw = topic.keywords;
    const nature = detectTopicNature(kw);
    const standards = detectStandards(kw);
    const conceptGroups = buildConceptGroups(kw, T);
    const coreTerms = kw.filter(k => k.length > 3).slice(0, 8);
    const topConcepts = coreTerms.slice(0, 4);

    // Build specific, measurable learning objectives
    const learningObjectives = [
        `Define and distinguish: ${topConcepts.join(', ')}`,
        nature !== 'theoretical'
            ? `Solve ${T}-related computations and journal entries accurately`
            : `Explain the rules and principles governing ${T}`,
        standards.length > 0
            ? `Cite the relevant standard (${standards.slice(0, 2).join(', ').toUpperCase()}) and its key requirements`
            : `Identify the legal/regulatory basis for ${T}`,
        `Identify common CPALE exam traps and avoid typical mistakes on ${T}`,
        `Score 75%+ on a ${T} quiz with at least 20 items`,
    ];

    const phases: StudyPhase[] = [];

    // ── Phase 1: Foundation — "What is this and why does it matter?" ──
    phases.push({
        id: `${topicId}-p1`,
        title: 'Foundation',
        description: `Build your mental map of ${T}. Understand what it is, why it exists, and how it fits into ${S}. Don't memorize yet — just get the big picture.`,
        type: 'read',
        difficulty: 'foundation',
        goalText: `You can explain ${T} in your own words and know where it fits in ${S}.`,
        microGoals: [
            { id: `${topicId}-p1-g1`, text: `I can define ${T} in one sentence` },
            { id: `${topicId}-p1-g2`, text: `I know why ${T} matters for the CPALE` },
            { id: `${topicId}-p1-g3`, text: `I can list the ${Math.min(topConcepts.length, 3)} main concepts within ${T}` },
            { id: `${topicId}-p1-g4`, text: `I understand how ${T} connects to other ${S} topics` },
            { id: `${topicId}-p1-g5`, text: `I can identify the key terms and vocabulary of ${T}` },
        ],
        prompts: [
            {
                text: `Give me a clear, structured overview of "${T}" for the CPALE. Cover: (1) what it is, (2) why it matters, (3) the ${topConcepts.length} key concepts I need to know, and (4) how it connects to other ${S} topics.`,
                context: 'Big-picture overview',
            },
            {
                text: `Explain ${T} using a real-world analogy that makes it easy to remember. Then list the key terms I must know.`,
                context: 'Intuitive understanding',
            },
            {
                text: `What's the difference between ${topConcepts.length >= 2 ? `"${topConcepts[0]}" and "${topConcepts[1]}"` : `the main concepts in ${T}`}? When would I use each one?`,
                context: 'Key distinctions',
            },
        ],
        quickRef: [
            { label: 'Key Terms', content: coreTerms.join(' · ') },
            ...(standards.length > 0 ? [{ label: 'Relevant Standard', content: standards.map(s => s.toUpperCase()).join(', ') }] : []),
        ],
        estimatedMinutes: 10,
    });

    // ── Phase 2: Concept Deep-Dives — one per concept group ──
    conceptGroups.forEach((group, gi) => {
        const groupPrompts: PromptCard[] = [];
        group.terms.slice(0, 3).forEach(term => {
            groupPrompts.push(...buildConceptPrompts(term, T, nature));
        });

        // Add a comparison prompt if there are multiple terms
        if (group.terms.length >= 2) {
            groupPrompts.push({
                text: `Compare and contrast ${group.terms.slice(0, 3).map(t => `"${t}"`).join(', ')} — what's the same, what's different, and when does each apply?`,
                context: 'Comparison & distinction',
            });
        }

        phases.push({
            id: `${topicId}-p2-${gi}`,
            title: `Deep Dive: ${group.label}`,
            description: `Master the details of ${group.terms.slice(0, 3).join(', ')}. Understand the rules, exceptions, and how they're tested in the CPALE.`,
            type: 'chat',
            difficulty: 'intermediate',
            goalText: `You can explain ${group.terms.slice(0, 2).join(' and ')} with confidence and know the exceptions.`,
            microGoals: [
                ...group.terms.slice(0, 3).map((term, ti) => ({
                    id: `${topicId}-p2-${gi}-g${ti}`,
                    text: `I understand "${term}" — definition, rules, and exceptions`,
                })),
                { id: `${topicId}-p2-${gi}-g-compare`, text: `I can compare and contrast the key concepts in ${group.label}` },
                { id: `${topicId}-p2-${gi}-g-apply`, text: `I can apply ${group.label} rules to a simple scenario` },
            ].slice(0, Math.max(5, group.terms.slice(0, 3).length + 2)),
            prompts: groupPrompts.slice(0, 5), // Cap at 5 prompts per phase
            estimatedMinutes: Math.max(10, group.terms.length * 5),
        });
    });

    // ── Phase 3: Standards & Rules (if applicable) ──
    if (standards.length > 0 || nature === 'theoretical') {
        const stdList = standards.length > 0 ? standards.map(s => s.toUpperCase()).join(', ') : 'the governing rules';
        phases.push({
            id: `${topicId}-p3`,
            title: 'Standards & Rules',
            description: `Know exactly which standard or law applies to ${T} and what it requires. Exams frequently test specific standard requirements.`,
            type: 'chat',
            difficulty: 'intermediate',
            goalText: `You can cite ${stdList} and explain its key recognition, measurement, and disclosure rules.`,
            microGoals: [
                { id: `${topicId}-p3-g1`, text: `I know which standard/law governs ${T}` },
                { id: `${topicId}-p3-g2`, text: 'I can list the recognition criteria' },
                { id: `${topicId}-p3-g3`, text: 'I know the measurement rules (initial & subsequent)' },
                { id: `${topicId}-p3-g4`, text: 'I know the key disclosure requirements' },
                { id: `${topicId}-p3-g5`, text: 'I can identify exceptions or special cases in the standard' },
            ],
            prompts: [
                {
                    text: `For ${T}, what does ${stdList} specifically require? Break it down into: (1) Scope, (2) Recognition, (3) Measurement, (4) Presentation, (5) Disclosure.`,
                    context: 'Standard requirements breakdown',
                },
                {
                    text: `What are the most frequently tested rules from ${stdList} in the CPALE? Give me the top 5 rules I must memorize.`,
                    context: 'Exam-critical rules',
                },
                {
                    text: `Are there any recent changes or amendments to the rules on ${T}? What's the old treatment vs the current treatment?`,
                    context: 'Recent changes',
                },
            ],
            quickRef: standards.length > 0 ? [
                { label: 'Standard', content: standards.map(s => s.toUpperCase()).join(', ') },
                { label: 'Remember', content: 'Recognition → Measurement → Presentation → Disclosure' },
            ] : [],
            estimatedMinutes: 12,
        });
    }

    // ── Phase 4: Worked Examples & Computation Practice ──
    phases.push({
        id: `${topicId}-p4`,
        title: nature === 'theoretical' ? 'Application & Scenarios' : 'Worked Problems',
        description: nature === 'theoretical'
            ? `Apply ${T} rules to realistic scenarios. Practice identifying the correct treatment, classification, or answer in exam-style situations.`
            : `Solve progressively harder problems on ${T}. Start with basic computations, then tackle board-exam-level problems.`,
        type: 'practice',
        difficulty: 'advanced',
        goalText: nature === 'theoretical'
            ? `You can apply ${T} rules to unfamiliar scenarios and choose the correct answer.`
            : `You can solve ${T} problems at board-exam difficulty without looking at notes.`,
        microGoals: nature !== 'theoretical' ? [
            { id: `${topicId}-p4-g1`, text: 'I solved an easy problem correctly' },
            { id: `${topicId}-p4-g2`, text: 'I solved a medium problem correctly' },
            { id: `${topicId}-p4-g3`, text: 'I attempted a hard/board-exam-level problem' },
            { id: `${topicId}-p4-g4`, text: 'I can set up the computation without looking at notes' },
            { id: `${topicId}-p4-g5`, text: 'I identified and avoided a common computation trap' },
        ] : [
            { id: `${topicId}-p4-g1`, text: 'I correctly analyzed a basic scenario' },
            { id: `${topicId}-p4-g2`, text: 'I handled a scenario with an exception or twist' },
            { id: `${topicId}-p4-g3`, text: 'I can explain my reasoning step by step' },
            { id: `${topicId}-p4-g4`, text: 'I spotted a trick question and chose the correct answer' },
            { id: `${topicId}-p4-g5`, text: 'I applied the rules to a multi-concept scenario' },
        ],
        prompts: nature !== 'theoretical' ? [
            {
                text: `Give me a step-by-step worked example on ${topConcepts[0] || T}. Show the setup, computation, and journal entry. Then give me a similar problem to try on my own (hide the answer).`,
                context: 'Guided practice → solo attempt',
            },
            {
                text: `Create a board-exam-level problem on ${T} involving ${topConcepts.slice(0, 2).join(' and ')}. Don't show the answer — let me try first.`,
                context: 'Exam-level challenge',
            },
            {
                text: `Show me 3 common computation mistakes students make on ${T} and how to avoid them.`,
                context: 'Error prevention',
            },
        ] : [
            {
                text: `Give me 3 realistic CPALE-style scenarios on ${T}, ranging from easy to tricky. For each, ask me what the correct treatment is before revealing the answer.`,
                context: 'Scenario analysis practice',
            },
            {
                text: `What are the trickiest edge cases and exceptions in ${T} that the CPALE loves to test? Give me a scenario for each.`,
                context: 'Edge cases & traps',
            },
            {
                text: `Create a "spot the error" exercise: give me a scenario where ${T} is applied incorrectly, and let me find the mistake.`,
                context: 'Critical thinking',
            },
        ],
        estimatedMinutes: 20,
    });

    // ── Phase 5: Quiz — test your knowledge ──
    phases.push({
        id: `${topicId}-p5`,
        title: 'Quiz Challenge',
        description: `Put your ${T} knowledge to the test. Take a 20-item multiple choice quiz and aim for 75%+ to prove you're ready.`,
        type: 'quiz',
        difficulty: 'advanced',
        goalText: `Pass a 20-item ${T} quiz with 75%+ score.`,
        microGoals: [
            { id: `${topicId}-p5-g1`, text: `Take a 20-item ${T} quiz (tap to start)` },
            { id: `${topicId}-p5-g2`, text: 'Score 60%+ on fundamentals' },
            { id: `${topicId}-p5-g3`, text: 'Score 75%+ on application-level questions' },
            { id: `${topicId}-p5-g4`, text: `Take a 20-item hard ${T} quiz` },
            { id: `${topicId}-p5-g5`, text: 'Score 75%+ on the hard quiz' },
        ],
        prompts: [
            {
                text: `Generate a 20-item multiple choice quiz on ${T} at medium difficulty. Mix theory and ${nature !== 'theoretical' ? 'computation' : 'application'} questions.`,
                context: 'Standard quiz',
            },
            {
                text: `Generate a 20-item hard multiple choice quiz on ${T} focusing on ${topConcepts.slice(0, 3).join(', ')}. Board-exam style.`,
                context: 'Hard mode',
            },
        ],
        quickRef: [
            { label: 'Format', content: '20 multiple choice items' },
            { label: 'Target', content: '75%+ to pass' },
        ],
        estimatedMinutes: 25,
    });

    // ── Phase 6: Review & Reinforce ──
    phases.push({
        id: `${topicId}-p6`,
        title: 'Review & Memorize',
        description: `Reinforce what you've learned. Review mistakes from your quiz, get mnemonics for tricky rules, and build a personal cheat sheet for ${T}.`,
        type: 'chat',
        difficulty: 'advanced',
        goalText: `You have a personal summary of ${T} and can recall key rules from memory.`,
        microGoals: [
            { id: `${topicId}-p6-g1`, text: 'I reviewed my quiz mistakes and understand why I got them wrong' },
            { id: `${topicId}-p6-g2`, text: 'I have mnemonics or memory aids for the hardest rules' },
            { id: `${topicId}-p6-g3`, text: `I can explain ${T} to someone else without notes` },
            { id: `${topicId}-p6-g4`, text: `I created a personal cheat sheet or summary for ${T}` },
            { id: `${topicId}-p6-g5`, text: 'I can recall the key rules without looking at my notes' },
        ],
        prompts: [
            {
                text: `I just finished studying ${T}. Give me a concise cheat sheet with: (1) key definitions, (2) ${nature !== 'theoretical' ? 'formulas and journal entries' : 'critical rules'}, (3) common exam traps, and (4) mnemonics to remember everything.`,
                context: 'Personal cheat sheet',
            },
            {
                text: `Give me mnemonics or memory tricks for the hardest parts of ${T}. I need to remember ${topConcepts.slice(0, 3).join(', ')}.`,
                context: 'Memory aids',
            },
            {
                text: `Quiz me verbally on ${T} — ask me 5 rapid-fire questions and tell me if I'm right or wrong after each one.`,
                context: 'Rapid recall practice',
            },
        ],
        estimatedMinutes: 12,
    });

    // ── Phase 7: Mastery Exam ──
    phases.push({
        id: `${topicId}-p7`,
        title: 'Mastery Exam',
        description: `You've completed all study phases! Take the ${topic.finalExam.questionCount}-item mastery exam. Pass with ${topic.finalExam.passingScore}% to master this topic and unlock the next one.`,
        type: 'exam',
        difficulty: 'exam-level',
        goalText: `Pass the ${T} mastery exam with ${topic.finalExam.passingScore}%+ and unlock the next topic.`,
        microGoals: [],
        prompts: [],
        quickRef: [
            { label: 'Exam', content: `${topic.finalExam.questionCount} questions · ${topic.finalExam.passingScore}% to pass` },
            { label: 'Reward', content: 'Bonus tokens + next topic unlocked' },
        ],
        estimatedMinutes: topic.finalExam.duration,
    });

    const totalMinutes = phases.reduce((s, p) => s + p.estimatedMinutes, 0);

    return {
        topicId,
        subjectId,
        title: T,
        overview: topic.description,
        learningObjectives,
        phases,
        conceptGroups,
        totalMinutes,
    };
}
